import React, { useEffect, useRef, useState } from 'react'
import ReactGA from 'react-ga'
import FormControl from '@material-ui/core/FormControl'
import Paper from '@material-ui/core/Paper'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import moment from 'moment'

import { DateRangePicker } from './DateRangePicker'
import DownloadContent from './Download/Content'
import Error from './Error'
// import { FakeDoor } from './FakeDoor'
import { Filter } from './Filter'
import { Graph } from './Graph'
import { GraphLegend } from './GraphLegend'
import { GrowthFiguresBar } from './GrowthFigures/GrowthFiguresBar'
import Form from './Marketo/Form'
import Success from './Marketo/Success'
import { SelectedFiltersBar } from './SelectedFilterOptions/SelectedFiltersBar'
import StyledButton, { ButtonThemeType } from './StyledButton'
import StyledDialog from './StyledDialog'
import StyledModal from './StyledModal'
import StyledTooltip from './StyledTooltip'
import {
  GROUP_TYPE,
  indexdetails,
  indextypes,
  regionDisabledOptions,
  regions,
  stateDisabledOptions,
  states,
} from '../constants/Filter'
import { defaultFigures } from '../constants/GrowthFigures'
import RefiIndexMethodologyDoc from '../docs/PEXA Refinance Index Methodology.pdf'
import { ReactComponent as DownloadIcon } from '../img/download.svg'
import { filterData, getNextUpdateDate } from '../services/dataFilter'
import transformData from '../services/dataTransform'
import { EmptyObj, Index, IndexDTO, RefinanceIndexDTO } from '../store/StateObjects'

import '../styles/container.css'

export interface ContextType {
  indexData: Index[]
  indexDetailType: string
  groupBy: GROUP_TYPE
  indexType: string[]
  state: string[]
  selectedRegion: string[]
  selected: string[]
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: '0px 8px 0px 0px',
      minWidth: 220,
      [theme.breakpoints.down('sm')]: {
        margin: '16px 8px 8px 0px',
        minWidth: '100%',
      },
    },
    contactButton: {
      margin: '65px 8px 55px 0px',
      minWidth: 220,
      [theme.breakpoints.down('sm')]: {
        margin: '12px 8px 12px 0px',
        minWidth: '100%',
      },
    },
    filterLabelContainer: {
      paddingTop: 20,
      display: 'inline-flex',
      minWidth: 240,
      fontFamily: 'Quicksand',
      fontSize: 24,
      fontWeight: 600,
      lineHeight: '32px',
      [theme.breakpoints.down('md')]: {
        minWidth: 120,
      },
    },
    selectInput: {
      color: '#4C4D68',
      fontWeight: 300,
      fontFamily: 'Libre Franklin',
      height: 48,
      lineHeight: '24px',
      minWidth: 240,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
  }),
)

const defaultFilter = {
  indexDetailType: indexdetails[0].value,
  state: states[0].value,
  indexType: indextypes[0].value,
  regions: regions[0].value,
}

const DEFAULT_DATE_RANGE = {
  defaultStartDate: moment(new Date()).subtract(11, 'months').startOf('month').toDate(),
  defaultEndDate: new Date(),
}

export const Context = React.createContext<ContextType>({
  indexData: [],
  indexDetailType: defaultFilter.indexDetailType,
  groupBy: GROUP_TYPE.STATE,
  indexType: [defaultFilter.indexType],
  state: [defaultFilter.state],
  selectedRegion: [defaultFilter.regions],
  selected: [],
})

export interface RefinanceIndexProps {
  refiObj: RefinanceIndexDTO
  hasError: boolean
}

export const RefinanceIndex: React.FC<RefinanceIndexProps> = ({
  refiObj = EmptyObj,
  hasError,
}: RefinanceIndexProps) => {
  const [indexData, setIndexData] = useState<Index[]>([])
  const [growthFigureData, setGrowthFigureData] = useState(defaultFigures)
  const [indexDetailType, setIndexDetailType] = useState(defaultFilter.indexDetailType)
  const [stateFilter, setStateFilter] = useState([defaultFilter.state])
  const [indexType, setIndexType] = useState([defaultFilter.indexType])
  const [region, setRegion] = useState([defaultFilter.regions])

  const [dateSelectDisabled] = useState(false)
  const [showMoreText, setShowMoreText] = useState(false)
  const [resetDatePicker, setResetDatePicker] = useState(new Date().getTime().toString())

  const { defaultStartDate, defaultEndDate } = DEFAULT_DATE_RANGE
  const minDateRef = useRef<Date>()
  const maxDateRef = useRef<Date>()

  const [selectedStartDate, setSelectedStartDate] = useState<Date>(defaultStartDate)
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(defaultEndDate)
  const [showModal, setShowModal] = useState(false)
  const [showMarketoForm, setShowMarketoForm] = useState(false)

  const onChangeDateFilter = (start: Date, end: Date) => {
    setSelectedStartDate(start)
    setSelectedEndDate(end)
  }

  const fireGAEvent = (eventAction: string, eventLabel: string) => {
    ReactGA.event({
      category: 'Refinance Index',
      action: eventAction,
      label: eventLabel,
    })
  }

  const handleShowText = (selection: boolean) => {
    setShowMoreText(selection)
    fireGAEvent('Text', selection ? 'Show more' : 'Show less')
  }

  const handleIndexDetailType = (type: string) => {
    setIndexDetailType(type)
    fireGAEvent('Index detail type', `${type} selected`)
  }

  const showMore = () => handleShowText(true)
  const showLess = () => handleShowText(false)

  const handleClickDownload = () => setShowModal(true)
  const handleOpenEmail = () => {
    window.open('mailto:insights@pexa.com.au')
  }

  /* const handleCheckBox = (event: React.MouseEvent, eventAction: string, eventLabel: string) => {
    setShowMarketoForm(true)
    fireGAEvent(eventAction, eventLabel)
  } */

  const getMinDate = (data: IndexDTO[]): Date => {
    const firstIndexWeek = data.slice(0, 1).pop()?.week
    return firstIndexWeek ? moment(firstIndexWeek, 'DD/MM/YYYY').set('date', 1).toDate() : new Date('2018-10-01')
  }

  const getMaxDate = (data: IndexDTO[]): Date => {
    const lastIndexWeek = data.slice(-1).pop()?.week

    return lastIndexWeek ? moment(lastIndexWeek, 'DD/MM/YYYY').toDate() : new Date()
  }

  useEffect(() => {
    const { index: data } = refiObj
    if (data?.length > 0 && !minDateRef.current) {
      minDateRef.current = getMinDate(data)
    }
    if (data?.length > 0 && !maxDateRef.current) {
      maxDateRef.current = getMaxDate(data)
    }

    const filteredData = filterData(data, stateFilter, region, indexType, selectedStartDate, selectedEndDate)
    setIndexData(transformData(filteredData))
  }, [hasError, indexType, refiObj, region, selectedEndDate, selectedStartDate, stateFilter])

  const classes = useStyles()
  const mediaMobile = useMediaQuery('(min-width:600px)')

  const clearFilters = () => {
    setStateFilter([defaultFilter.state])
    setIndexType([defaultFilter.indexType])
    setRegion([defaultFilter.regions])
    onChangeDateFilter(defaultStartDate, defaultEndDate)
    setResetDatePicker(new Date().getTime().toString())
  }

  const getGroupBy = () => {
    if (stateFilter.length > 1) {
      return GROUP_TYPE.STATE
    }
    return region.length > 1 ? GROUP_TYPE.REGION : GROUP_TYPE.INDEX_TYPE
  }

  const groupBy = getGroupBy()

  const getDataByGroup = () => {
    switch (groupBy) {
      case GROUP_TYPE.INDEX_TYPE:
        return indexType
      case GROUP_TYPE.REGION:
        return region
      default:
        return stateFilter
    }
  }

  // Update growth data based on currently selected data
  const updateGrowthData = () => {
    if (indexData.length === 0 && growthFigureData !== defaultFigures) {
      setGrowthFigureData(defaultFigures)
      return
    }
    const tempIndexDetailType = indexDetailType === 'VALUE' ? 'value' : 'volume'

    if (indexData.length > 0 && growthFigureData !== indexData[indexData.length - 1][tempIndexDetailType]) {
      setGrowthFigureData(indexData[indexData.length - 1][tempIndexDetailType])
    }
  }
  updateGrowthData()
  const context: ContextType = {
    indexData,
    indexDetailType,
    groupBy,
    indexType,
    state: stateFilter,
    selectedRegion: region,
    selected: getDataByGroup(),
  }
  return (
    <Context.Provider value={context}>
      <Paper square elevation={10} className="paper">
        <div className="page-title-container">
          <div className="page-title">Refinance Index</div>
          <StyledButton
            className={classes.formControl}
            testId="download-open-modal-button"
            handleClick={handleClickDownload}
            startIcon={DownloadIcon}
            width={200}
            style={{ marginRight: '27px' }}
          >
            Download
          </StyledButton>
        </div>
        <div className="index-toggle">
          <button
            type="button"
            aria-label="View volume index"
            className={['index-toggle-volume', indexDetailType === 'VOLUME' ? 'index-toggle-selected' : ''].join(' ')}
            data-testid="index-toggle-volume"
            onClick={(event) => {
              handleIndexDetailType(event.currentTarget.innerText)
            }}
          >
            VOLUME
          </button>
          <button
            type="button"
            aria-label="View value index"
            className={['index-toggle-value', indexDetailType === 'VALUE' ? 'index-toggle-selected' : ''].join(' ')}
            data-testid="index-toggle-value"
            onClick={(event) => {
              handleIndexDetailType(event.currentTarget.innerText)
            }}
          >
            VALUE
          </button>
        </div>
        <div className="last-update-date">
          Last updated {refiObj.last_updated_date} (next available{' '}
          {refiObj.next_updated_date !== '' ? refiObj.next_updated_date : getNextUpdateDate(refiObj.last_updated_date)})
        </div>

        <div className="filters">
          <div className="label-container">
            <div className={classes.filterLabelContainer}>
              <div className="filter-label-text">Filter Index Data</div>
            </div>
          </div>
          <div className="filters-container">
            <Filter
              label="State"
              multiple={!(indexType.length > 1 || region.length > 1)}
              filterValue={stateFilter}
              onChange={setStateFilter}
              styles={classes}
              filterData={states}
              dataTestId="state-select"
              filterBase={region}
              disabledOptions={stateDisabledOptions}
            />

            <Filter
              label="Region"
              multiple={!(indexType.length > 1 || stateFilter.length > 1)}
              filterValue={region}
              onChange={setRegion}
              styles={classes}
              filterData={regions}
              tooltip
              toolTipContent={
                <span>
                  filter by <b>greater capital city area</b> and <b>rest of state</b>{' '}
                </span>
              }
              dataTestId="region-select"
              filterBase={stateFilter}
              disabledOptions={regionDisabledOptions}
            />

            <FormControl variant="outlined" className={classes.formControl} disabled={dateSelectDisabled}>
              <label htmlFor="date-select">
                Date
                <StyledTooltip label="Date range" toolTipContent="filter by the date ranges provided or customise" />
              </label>
              <DateRangePicker
                minDate={minDateRef.current}
                maxDate={maxDateRef.current}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                onChange={onChangeDateFilter}
                styles={classes}
                key={resetDatePicker}
              />
            </FormControl>

            <Filter
              label="Index Type"
              multiple={!(stateFilter.length > 1 || region.length > 1)}
              filterValue={indexType}
              onChange={setIndexType}
              styles={classes}
              filterData={indextypes}
              tooltip
              toolTipContent={
                <span>
                  <b>Base </b> - shows weekly changes in refinancing volume or value
                  <br />
                  <b>Seasonally adjusted </b> - the base view with seasonal variations smoothed to show underlying
                  trends more clearly
                </span>
              }
              dataTestId="index-type-select"
            />
          </div>
        </div>

        {/* <FakeDoor handleClick={handleCheckBox} showMarketoForm={showMarketoForm} /> */}

        <SelectedFiltersBar
          selectedStates={stateFilter}
          setState={setStateFilter}
          selectedRegions={region}
          setRegion={setRegion}
          selectedIndexTypes={indexType}
          setIndexType={setIndexType}
          clearFilters={clearFilters}
        />

        <div className="refinance-index-graph">
          {!hasError ? (
            <>
              <p className="graph-header" data-testid="graph-header">
                {indexDetailType.charAt(0) + indexDetailType.substring(1).toLowerCase()} of Refinances
              </p>
              <Graph mobile={!mediaMobile} indexData={indexData} indexDetailType={indexDetailType} groupBy={groupBy} />
              <GraphLegend type={groupBy} selected={getDataByGroup()} />
            </>
          ) : (
            <Error />
          )}
        </div>
        <GrowthFiguresBar
          growthFigureData={growthFigureData}
          indexDetailType={indexDetailType}
          isAvailable={stateFilter.length <= 1 && region.length <= 1 && indexType.length <= 1}
        />
        <div className="divider-refi-content" />
        <div className="refi-content">
          <div className="heading-text">What is the Refinance Index?</div>
          <div className="heading-text-content">
            PEXA’s Refinance Index provides timely insights into property refinancing in Australia<sup>1</sup>. The
            interactive tool uses PEXA’s comprehensive data to show market performance and highlight where growth is
            occurring<sup>2</sup>.
          </div>
          <div className="heading-text">How to use the PEXA Refinance Index?</div>
          <div className="heading-text-content">
            View the index data by <b>volume</b>
            <sup>3</sup> (number of refinances) or <b>value</b>
            <sup>4</sup> (median monetary value of refinances). Insights are available at a national, state and regional
            level
            <sup>5</sup>. Select multiple states to compare trends.
            {!showMoreText ? (
              <>
                <span>.. </span>
                <br />
                <button type="button" className="read-more-less" onClick={showMore}>
                  Read more
                </button>
              </>
            ) : null}
          </div>
          {showMoreText ? (
            <div className="heading-text-content">
              Index data is represented in two views<sup>6</sup>;
              <br />
              <ul>
                <li>
                  <b>Base</b> - showing weekly changes in refinancing volume or value. For example, if the index rises
                  from 120 points to 140 points (increase of 20-points), this represents an increase of 20% compared to
                  the starting point of the index.
                </li>
                <li>
                  <b>Seasonally adjusted </b> - the base view with seasonal variations smoothed to show underlying
                  trends more clearly.
                </li>
              </ul>
              <br />
              The index is updated every <b>Tuesday</b>, displaying the prior week’s results (week ending Sunday). Back
              series data is available from 15 October 2018. See full index methodology&nbsp;
              <a target="_blank" rel="noreferrer" href={RefiIndexMethodologyDoc}>
                here
              </a>
              .&nbsp;
              <br />
              <button type="button" className="read-more-less" onClick={showLess}>
                Read less
              </button>
            </div>
          ) : null}
          <StyledButton
            className={classes.contactButton}
            testId="contact-us-button"
            handleClick={handleOpenEmail}
            width={200}
            theme={ButtonThemeType.SECONDARY}
          >
            Contact Us
          </StyledButton>
        </div>
        <StyledModal open={showModal} setOpen={setShowModal} content={DownloadContent} />
        <StyledDialog
          open={showMarketoForm}
          setOpen={setShowMarketoForm}
          content={Success}
          contentMaxWidth={600}
          contentMaxHeight={520}
        />
        <StyledDialog
          id="fakedoormodal"
          open={showMarketoForm}
          setOpen={setShowMarketoForm}
          content={Form}
          backDropInvisible
          closeButtonWhite
        />
      </Paper>
    </Context.Provider>
  )
}
